/**
 * 错误日志工具
 * 用于统一记录和上报错误信息
 */

interface ErrorLog {
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  context?: any;
  url?: string;
  line?: number;
  column?: number;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 100; // 最多保存100条日志
  private enableConsole = true;

  /**
   * 记录错误
   */
  error(message: string, error?: Error, context?: any): void {
    const log: ErrorLog = {
      timestamp: new Date().toISOString(),
      type: 'error',
      message,
      stack: error?.stack,
      context
    };

    this.addLog(log);

    if (this.enableConsole) {
      console.error('[错误]', message, error || '', context || '');
    }
  }

  /**
   * 记录警告
   */
  warning(message: string, context?: any): void {
    const log: ErrorLog = {
      timestamp: new Date().toISOString(),
      type: 'warning',
      message,
      context
    };

    this.addLog(log);

    if (this.enableConsole) {
      console.warn('[警告]', message, context || '');
    }
  }

  /**
   * 记录信息
   */
  info(message: string, context?: any): void {
    const log: ErrorLog = {
      timestamp: new Date().toISOString(),
      type: 'info',
      message,
      context
    };

    this.addLog(log);

    if (this.enableConsole) {
      // console.log('[信息]', message, context || '');
    }
  }

  /**
   * 记录网络错误
   */
  networkError(url: string, error: any, context?: any): void {
    this.error(`网络请求失败: ${url}`, error, {
      url,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      ...context
    });
  }

  /**
   * 添加日志到队列
   */
  private addLog(log: ErrorLog): void {
    this.logs.push(log);
    
    // 保持日志队列不超过最大值
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  /**
   * 获取所有日志
   */
  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  /**
   * 获取最近的错误日志
   */
  getRecentErrors(count: number = 10): ErrorLog[] {
    return this.logs
      .filter(log => log.type === 'error')
      .slice(-count);
  }

  /**
   * 清空日志
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * 导出日志为JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * 设置是否在控制台输出
   */
  setEnableConsole(enable: boolean): void {
    this.enableConsole = enable;
  }
}

// 创建单例实例
let errorLoggerInstance: ErrorLogger | null = null;

/**
 * 获取错误日志实例
 */
export function getErrorLogger(): ErrorLogger {
  if (!errorLoggerInstance) {
    errorLoggerInstance = new ErrorLogger();
  }
  return errorLoggerInstance;
}

// 导出便捷方法
export const logger = {
  error: (message: string, error?: Error, context?: any) => getErrorLogger().error(message, error, context),
  warning: (message: string, context?: any) => getErrorLogger().warning(message, context),
  info: (message: string, context?: any) => getErrorLogger().info(message, context),
  networkError: (url: string, error: any, context?: any) => getErrorLogger().networkError(url, error, context),
  getLogs: () => getErrorLogger().getLogs(),
  getRecentErrors: (count?: number) => getErrorLogger().getRecentErrors(count),
  clearLogs: () => getErrorLogger().clearLogs(),
  exportLogs: () => getErrorLogger().exportLogs()
};

export { ErrorLogger };
export type { ErrorLog };
